import {
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material'
import { ConfirmDialog, Icon, SnackBarContext, CompanySelect } from 'components'
import { TextField } from 'components/textField'
import { SwedSec } from 'lib/swedsec/swedsec'
import { inject, observer } from 'mobx-react'
import { Course } from 'models'
import { Program } from 'models/program'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {  Pages, RoutingParams } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import { DataStatus } from 'types'

export interface IProgramFormProps {
  routingStore?: RoutingStore,
  authStore?:    AuthStore,

}

export const ProgramForm = inject('routingStore', 'authStore')(observer((props: IProgramFormProps) => {
  const { t }     = useTranslation();
  const { toast } = React.useContext(SnackBarContext);
  const { routingStore, authStore } = props;
  const params: RoutingParams = useParams<RoutingParams>();
  
  //const [startDate, setStartDate] = useState<Date | null>(new Date());
  //const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [program, setProgram] = useState<Program>(undefined);
  const [courses, setCourses] = useState<Course[]>([]);
  const [openConfirm,      setOpenConfirm]      = React.useState(false);
  const [courseInputValue, setCourseInputValue] = useState('');

  useEffect(() => {
    Program.get(Number(params.id)).then(async (p: Program) => {
      setProgram(p);
      try {
        await p.loadCourses();
      } catch (error) {
        console.error(error);
        toast(t('toast_error_load_courses'), 'error');
      }
    })
  }, [params.id]);

  useEffect(() => {
    Course.get().then((c: Course[]) => setCourses(c));
  }, []);

  const statusButtonAction = async () => {
      const old_status: DataStatus = program.status;
      const target_status: DataStatus = program.status === DataStatus.Published ? DataStatus.Archived : DataStatus.Published;
      if(await program?.set('status', target_status, true)) toast(t('program_status_changed'), 'success');
      else {
        program?.set('status', old_status, false);
        console.error('Publish failed. Do something!');
        toast(t('toast_error_status_change_fail'), 'error');
      }
  }

  const statusButtonLabel = () => {
      if (program?.status === DataStatus.Draft)
        return t('publish');
      else if (program?.status === DataStatus.Published)
        return t('archive');
      else if (program?.status === DataStatus.Archived)
        return t('publish');
  }

  return (
    <Container maxWidth='md'>
      <Grid item xs={6}>
        <Typography variant='h4' mb={4}>
          {t('program_settings')}
        </Typography>
        <FormControl fullWidth margin='normal'>
          <FormLabel>{t('program_name')}</FormLabel>
          <TextField
            hiddenLabel
            debounced
            delay    = {300}
            value    = {program?.title || ''}
            variant  = 'outlined'
            onChange = {(e) => program.set('title', e.target.value, true)}
          />
        </FormControl>

        <FormControl fullWidth margin='normal'>
          <FormLabel>{t('program_description')}</FormLabel>
          <TextField
            hiddenLabel
            multiline
            debounced
            minRows     = '3'
            delay       = {300}
            sx          = {{ p: 0 }}
            variant     = 'outlined'
            placeholder = {t('program_description_placeholder')}
            value       = {program?.desc || ''}
            onChange    = {(e) => program.set('desc', e.target.value, true)}
          />
        </FormControl>


        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel>{t('category')}</FormLabel>
          <TextField
            hiddenLabel
            debounced
            delay      = {300}
            value      = {program?.category || ''}
            variant    = 'outlined'
            inputProps = {{ maxLength: 40 }}
            onChange   = {(e) => program.set('category', e.target.value, true)}
          />
        </FormControl>
        
        <FormControl margin='normal' fullWidth>
          <FormLabel>{t('aku_type')}</FormLabel>
          <Select
            displayEmpty
            id          = "aku_type"
            value       = {program?.aku_type || ""}
            renderValue = {(selected) => {
              if (!selected) {
                return <span style={{color: "#BBB"}}>{t("aku_type_placeholder")}</span>;
              }
              return selected;
            }}
  
          >
            {Object.values(SwedSec.AKUType).map(type => (
              <MenuItem 
                key      = {type}
                value    = {type}
                onClick  = {() => {
                  // Note: this is moved to an onClick rather than
                  // onChange on the <Select> because MUI doesnt 
                  // allow deselection
                  program.set(
                    "aku_type", 
                    type === program.aku_type 
                      ? null 
                      : type, 
                    true
                  );
                }}
              >
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl margin='normal' fullWidth>
          <FormLabel>{t('assign_courses')}</FormLabel>
          <Autocomplete
            multiple
            limitTags            = {4}
            id                   = 'program-courses'
            options              = {courses.filter(c => c.status === DataStatus.Published)}
            // disabled             = {program?.status !== DataStatus.Draft}
            isOptionEqualToValue = {(option: Course, value: Course) =>
              option.id === value.id
            }
            getOptionLabel = {(option) => option.title}
            value          = {program ? [...program.courses] : []}   // This weird construction is necessary cause MUI otherwise triggers an error within the mobx observer proxy
            inputValue     = {courseInputValue}
            noOptionsText  = {t('no_courses_found')}
            onClose        = {() => setCourseInputValue('')}
            onChange       = {(event, values, reason, details) => {
              if (reason === 'selectOption')
                program.linkCourse(details.option as Course);
              else program.unlinkCourse(details.option as Course);
            }}
            onInputChange  = {(event, value, reason) => {
              if (reason === 'reset') return;
              setCourseInputValue(value);
            }}
            renderInput = {(params) => (
              <TextField
                {...params}
                hiddenLabel
                variant     = 'outlined'
                placeholder = {t('search_for_course')}
              />
            )}
          />
        </FormControl>

        {!authStore.getCurUser.external_editor && 
        <FormControl margin='normal' fullWidth>
          <CompanySelect content={program} />
        </FormControl>}

        <Box display='flex' justifyContent='space-between' mt={4}>
          <Button
            onClick   = {() => setOpenConfirm(true)}
            color     = 'secondary'
            variant   = 'outlined'
            startIcon = {<Icon iconId='bin' />}
          >
            {t('delete_program')}
          </Button>
          <Tooltip title={!program?.canPublish ? t('publish_package_disabled') : ''}>
            <Box>
            <Button
              onClick  = {statusButtonAction}
              disabled = {!program?.canPublish}
              variant  = 'contained'
            >
              {statusButtonLabel()} 
            </Button>
          </Box>
          </Tooltip>

          <ConfirmDialog
            title             = {t('confirm_delete_title') + '?'}
            open              = {openConfirm}
            onClose           = {() => setOpenConfirm(false)}
            onCancel          = {() => setOpenConfirm(false)}
            content           = {t('confirm_delete_program_body')}
            confirmButtonText = {t('yes')}
            cancelButtonText  = {t('cancel')}
            onConfirm         = {async () => {
              if (await program.remove()) {
                routingStore.push(Pages.Programs);
              } else {
                toast(t('toast_error_remove_program'), 'error');
                console.error('Failed to remove program');
              }
            }}
          />
        </Box>
      </Grid>
    </Container>
  );
}));
